import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Search,
  Users,
  Briefcase,
  CheckSquare,
  BarChart3,
  LifeBuoy,
  LayoutDashboard,
  ArrowRight,
} from "lucide-react";

const PAGE_ITEMS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, keywords: "home overview" },
  { id: "analytics", label: "Analytics", icon: BarChart3, keywords: "metrics reports charts" },
  { id: "contacts", label: "Contacts", icon: Users, keywords: "people customers leads" },
  { id: "deals", label: "Deals", icon: Briefcase, keywords: "pipeline opportunities" },
  { id: "tasks", label: "Task inbox", icon: CheckSquare, keywords: "todo follow up" },
  { id: "support", label: "Support", icon: LifeBuoy, keywords: "tickets help" },
  { id: "revenue", label: "Revenue Ops", icon: BarChart3, keywords: "revenue billing" },
  { id: "finances", label: "Finances", icon: BarChart3, keywords: "money invoices" },
  { id: "engagement", label: "Engagement", icon: Users, keywords: "retention" },
  { id: "marketing", label: "Marketing", icon: BarChart3, keywords: "campaigns links" },
  { id: "activity", label: "Activity", icon: BarChart3, keywords: "timeline feed" },
  { id: "settings", label: "Settings", icon: LayoutDashboard, keywords: "preferences config" },
];

function norm(s) {
  return (s || "").toLowerCase().trim();
}

export default function CommandPalette({
  open,
  onClose,
  contacts = [],
  deals = [],
  tasks = [],
  onNavigate,
  onOpenContact,
  onOpenDeal,
}) {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  const results = useMemo(() => {
    const query = norm(q);
    const items = [];

    if (!query || "analytics reports metrics".includes(query) || query.length <= 2) {
      PAGE_ITEMS.forEach((p) => {
        const hay = `${p.label} ${p.keywords}`.toLowerCase();
        if (!query || hay.includes(query)) {
          items.push({ type: "page", id: p.id, label: p.label, sub: "Navigate", icon: p.icon });
        }
      });
    }

    contacts.forEach((c) => {
      const hay = norm(`${c.name} ${c.email} ${c.companyName}`);
      if (!query || hay.includes(query)) {
        items.push({
          type: "contact",
          id: c.id,
          label: c.name || c.email,
          sub: c.companyName || c.email,
          data: c,
        });
      }
    });

    deals.forEach((d) => {
      const hay = norm(`${d.title} ${d.companyName} ${d.stage}`);
      if (!query || hay.includes(query)) {
        items.push({
          type: "deal",
          id: d.id,
          label: d.title || "Untitled deal",
          sub: d.stage || "Deal",
          data: d,
        });
      }
    });

    tasks.forEach((t) => {
      const hay = norm(`${t.title} ${t.description}`);
      if (!query || hay.includes(query)) {
        items.push({
          type: "task",
          id: t.id,
          label: t.title || "Task",
          sub: t.status === "open" ? "Open task" : t.status || "Task",
          data: t,
        });
      }
    });

    return items.slice(0, 12);
  }, [q, contacts, deals, tasks]);

  const run = useCallback(
    (item) => {
      if (!item) return;
      if (item.type === "page") onNavigate?.(item.id);
      else if (item.type === "contact") onOpenContact?.(item.data);
      else if (item.type === "deal") onOpenDeal?.(item.id);
      else if (item.type === "task") onNavigate?.("tasks");
      onClose?.();
      setQ("");
    },
    [onNavigate, onOpenContact, onOpenDeal, onClose]
  );

  useEffect(() => {
    if (open) {
      setQ("");
      setActive(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [q]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((i) => Math.min(i + 1, results.length - 1));
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((i) => Math.max(i - 1, 0));
      }
      if (e.key === "Enter" && results[active]) {
        e.preventDefault();
        run(results[active]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, results, active, run, onClose]);

  if (!open) return null;

  return (
    <div className="cmdk-overlay" onClick={onClose} role="presentation">
      <div className="cmdk" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal aria-label="Command menu">
        <div className="cmdk-input-wrap">
          <Search size={18} strokeWidth={1.75} className="cmdk-search-icon" />
          <input
            ref={inputRef}
            className="cmdk-input"
            placeholder="Search contacts, deals, tasks, pages…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-autocomplete="list"
          />
          <kbd className="cmdk-kbd">esc</kbd>
        </div>
        <div className="cmdk-results">
          {results.length === 0 ? (
            <div className="cmdk-empty">No results for &ldquo;{q}&rdquo;</div>
          ) : (
            results.map((item, i) => {
              const Icon =
                item.icon ||
                (item.type === "contact"
                  ? Users
                  : item.type === "deal"
                    ? Briefcase
                    : item.type === "task"
                      ? CheckSquare
                      : ArrowRight);
              return (
                <button
                  key={`${item.type}-${item.id}`}
                  type="button"
                  className={`cmdk-item ${i === active ? "active" : ""}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => run(item)}
                >
                  <span className="cmdk-item-icon">
                    <Icon size={16} strokeWidth={1.75} />
                  </span>
                  <span className="cmdk-item-body">
                    <span className="cmdk-item-label">{item.label}</span>
                    <span className="cmdk-item-sub">{item.sub}</span>
                  </span>
                  <span className="cmdk-item-hint">{item.type}</span>
                </button>
              );
            })
          )}
        </div>
        <div className="cmdk-footer">
          <span>
            <kbd>↑</kbd> <kbd>↓</kbd> navigate
          </span>
          <span>
            <kbd>↵</kbd> open
          </span>
        </div>
      </div>
    </div>
  );
}

export function useCommandPalette() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return { open, setOpen };
}
